import React, { useState } from 'react';
import { Trash2 } from 'lucide-react';
import AddItemModal from './ListBuilder/AddItemModal';

export interface ListBuilderItem {
  id: number;
  label: string;
  status?: string | null;
}

export interface ListBuilderConfig {
  idKey: string;
  labelKey: string;
  statusKey?: string;
  statusOptions?: { value: string; label: string }[];
  addButtonText?: string;
  addModalTitle?: string;
  noItemsText?: string;
}

export interface ListBuilderProps {
  id: string;
  label: string;
  value: ListBuilderItem[];
  onChange?: (value: ListBuilderItem[]) => void;
  availableItems: ListBuilderItem[];
  config: ListBuilderConfig;
  disabled?: boolean;
  required?: boolean;
  styles: Record<string, React.CSSProperties>;
}

const ListBuilder: React.FC<ListBuilderProps> = ({
  id: _id,
  label,
  value,
  onChange,
  availableItems,
  config,
  disabled = false,
  required = false,
  styles,
}) => {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  const handleStatusChange = (itemId: number, newStatus: string | null) => {
    if (!onChange) return;
    const updatedItems = value.map(item =>
      item.id === itemId ? { ...item, status: newStatus } : item
    );
    onChange(updatedItems);
  };

  const handleRemoveItem = (itemId: number) => {
    if (!onChange) return;
    const updatedItems = value.filter(item => item.id !== itemId);
    onChange(updatedItems);
  };

  const handleAddItems = (selectedIds: number[]) => {
    if (!onChange) return;
    const newItems = selectedIds
      .map(selectedId => availableItems.find(item => item.id === selectedId))
      .filter((item): item is ListBuilderItem => item !== undefined)
      .map(item => ({
        ...item,
        status: null, // Default status is null
      }));
    onChange([...value, ...newItems]);
    setIsAddModalOpen(false);
  };

  // Filter out items that are already in the list
  const filteredAvailableItems = availableItems.filter(
    item => !value.some(v => v.id === item.id)
  );

  const statusOptions = config.statusOptions || [];
  const hasStatusColumn = !!config.statusKey;

  return (
    <div style={styles.floatingFieldContainer}>
      <div
        style={{
          ...listBuilderStyles.container,
          ...(disabled ? listBuilderStyles.containerDisabled : {}),
        }}>
        {/* Header */}
        <div style={listBuilderStyles.header}>
          <label
            style={{
              ...listBuilderStyles.headerLabel,
              ...(disabled ? listBuilderStyles.headerLabelDisabled : {}),
            }}>
            {label}
            {required && <span style={{ color: 'red' }}>*</span>}
          </label>
        </div>

        {/* Table */}
        {value.length > 0 ? (
          <div style={listBuilderStyles.tableContainer}>
            <table style={listBuilderStyles.table}>
              <thead>
                <tr style={listBuilderStyles.tableHeaderRow}>
                  <th style={listBuilderStyles.tableHeaderCell}>
                    {config.labelKey}
                  </th>
                  {hasStatusColumn && (
                    <th style={listBuilderStyles.tableHeaderCell}>
                      {config.statusKey}
                    </th>
                  )}
                  <th style={listBuilderStyles.tableHeaderCellActions}></th>
                </tr>
              </thead>
              <tbody>
                {value.map(item => {
                  return (
                    <tr key={item.id} style={listBuilderStyles.tableRow}>
                      <td
                        style={{
                          ...listBuilderStyles.tableCell,
                          ...(disabled
                            ? listBuilderStyles.tableCellDisabled
                            : {}),
                        }}>
                        {item.label}
                      </td>
                      {hasStatusColumn && (
                        <td style={listBuilderStyles.tableCellStatus}>
                          <select
                            value={item.status || ''}
                            onChange={e => {
                              handleStatusChange(
                                item.id,
                                e.target.value || null
                              );
                            }}
                            disabled={disabled}
                            style={{
                              ...listBuilderStyles.statusSelect,
                              ...(disabled
                                ? listBuilderStyles.statusSelectDisabled
                                : {}),
                            }}>
                            <option value="">None</option>
                            {statusOptions.map(opt => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        </td>
                      )}
                      <td style={listBuilderStyles.tableCellActions}>
                        <button
                          type="button"
                          onClick={() => handleRemoveItem(item.id)}
                          disabled={disabled}
                          style={{
                            ...listBuilderStyles.deleteButton,
                            ...(disabled
                              ? listBuilderStyles.deleteButtonDisabled
                              : {}),
                          }}
                          onMouseEnter={e =>
                            !disabled &&
                            Object.assign(
                              e.currentTarget.style,
                              listBuilderStyles.deleteButtonHover
                            )
                          }
                          onMouseLeave={e =>
                            !disabled &&
                            Object.assign(
                              e.currentTarget.style,
                              listBuilderStyles.deleteButton
                            )
                          }
                          aria-label={`Remove ${item.label}`}>
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={listBuilderStyles.noItemsText}>
            {config.noItemsText || 'No items added'}
          </div>
        )}

        {/* Add Button */}
        <button
          type="button"
          onClick={() => setIsAddModalOpen(true)}
          disabled={disabled || filteredAvailableItems.length === 0}
          style={{
            ...listBuilderStyles.addButton,
            ...(disabled || filteredAvailableItems.length === 0
              ? listBuilderStyles.addButtonDisabled
              : {}),
          }}>
          <span style={listBuilderStyles.addButtonIcon}>+</span>
          {config.addButtonText || 'Add Item'}
        </button>
      </div>

      {/* Add Items Modal */}
      {isAddModalOpen && (
        <AddItemModal
          title={config.addModalTitle || 'Add Items'}
          availableItems={filteredAvailableItems}
          onConfirm={handleAddItems}
          onCancel={() => setIsAddModalOpen(false)}
        />
      )}
    </div>
  );
};

const listBuilderStyles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  containerDisabled: {
    opacity: 0.6,
    pointerEvents: 'none',
  },
  header: {
    marginBottom: '8px',
  },
  headerLabel: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#374151',
  },
  headerLabelDisabled: {
    color: '#9ca3af',
  },
  tableContainer: {
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    overflow: 'hidden',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  tableHeaderRow: {
    backgroundColor: '#f9fafb',
  },
  tableHeaderCell: {
    padding: '10px 12px',
    textAlign: 'left',
    fontSize: '12px',
    fontWeight: 600,
    color: '#6b7280',
    borderBottom: '1px solid #e5e7eb',
  },
  tableHeaderCellActions: {
    padding: '10px 12px',
    textAlign: 'right',
    fontSize: '12px',
    fontWeight: 600,
    color: '#6b7280',
    borderBottom: '1px solid #e5e7eb',
    width: '60px',
  },
  tableRow: {
    borderBottom: '1px solid #e5e7eb',
  },
  tableCell: {
    padding: '10px 12px',
    fontSize: '14px',
    color: '#374151',
  },
  tableCellDisabled: {
    color: '#9ca3af',
  },
  tableCellStatus: {
    padding: '10px 12px',
    width: '200px',
  },
  tableCellActions: {
    padding: '10px 12px',
    textAlign: 'right',
  },
  statusSelect: {
    width: '100%',
    padding: '6px 8px',
    fontSize: '14px',
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    backgroundColor: '#ffffff',
    color: '#374151',
    cursor: 'pointer',
  },
  statusSelectDisabled: {
    backgroundColor: '#f9fafb',
    color: '#9ca3af',
    cursor: 'not-allowed',
  },
  deleteButton: {
    padding: '6px 8px',
    backgroundColor: 'transparent',
    border: '1px solid #e5e7eb',
    borderRadius: '4px',
    color: '#dc2626',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s',
  },
  deleteButtonHover: {
    color: '#991b1b',
    backgroundColor: 'transparent',
    border: '1px solid #e5e7eb',
    borderRadius: '4px',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s',
  },
  deleteButtonDisabled: {
    color: '#d1d5db',
    cursor: 'not-allowed',
    border: '1px solid #f3f4f6',
    borderRadius: '4px',
  },
  addButton: {
    padding: '8px 16px',
    backgroundColor: '#ffffff',
    border: '1px solid #3b82f6',
    borderRadius: '6px',
    color: '#3b82f6',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    alignSelf: 'flex-start',
    transition: 'all 0.2s',
  },
  addButtonDisabled: {
    backgroundColor: '#f9fafb',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    color: '#9ca3af',
    cursor: 'not-allowed',
  },
  addButtonIcon: {
    fontSize: '18px',
    fontWeight: 600,
  },
  noItemsText: {
    padding: '32px',
    textAlign: 'center',
    color: '#9ca3af',
    fontSize: '14px',
    border: '1px dashed #d1d5db',
    borderRadius: '6px',
  },
};

export default React.memo(ListBuilder);
